import { useTranslation, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import { Helmet } from 'react-helmet-async';
import { useAgreement } from '../../hooks/useReview';
import { PAGES } from '../../constants/pages';
import PrintModal from '../../components/PrintModal/PrintModal';
import { useMenu } from '../../hooks/useMenu';
import Paragraph from '../../components/Paragraph/Paragraph';
import BulletList from '../../components/BulletList/BulletList';
import BulletItem from '../../components/BulletItem/BulletItem';
import useApplicationUid from '../../hooks/useApplicationUid';
import { useMetalStreamData } from '../../hooks/useMetalStreamData';
import useGtmHook from '../../util/hooks/useGtmHook';
import {
  ACCOUNT_STEP_COMPLETE,
  ACCOUNT_STEP_VIEW,
  APPLICATION_TERMS_ACCEPTED,
} from '../../util/gtmEvents';
import useStepPost from '../../hooks/useStepPost';
import Anchor from '../../components/Anchor/Anchor';
import { TAndCLink } from '../../components/ReviewElements';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { Heading } from '../../components/Heading';
import { FormWrap } from '../../components/FormContainer/FormContainer';
import DownloadIcon from '../../components/Icons/DownloadIcon';
import Checkbox from '../../components/Fields/Checkbox';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import {
  AGREEMENT_TYPES,
  BASIC_ACCOUNT_UPGRADE_TYPES,
} from '../../util/constants';
import {
  createAgreement,
  downloadPdfAgreement,
} from '../../store/actions/agreement/agreementActions';
import AgreementDisplay from '../../components/Agreements/AgreementDisplay';
import { metalStreamDataHelper } from '../../util/helpers/metalStreamDataHelper';
import { selectBankAccounts } from '../../store/selectors/bankAccountSelectors';
import { fetchBankAccounts } from '../../store/actions/bankAccount/bankAccountActions';
import themeColors from '../../assets/styles/themeColors';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { useApplicantData } from '../../hooks/useApplicantData';

const DownloadContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  cursor: pointer;
  margin-bottom: ${(props) => (props.mb ? `${props.mb}px` : 0)};
  margin-top: ${(props) => (props.mt ? `${props.mt}px` : 0)};
`;

const Strong = styled.strong`
  color: ${themeColors.colorLink};
  text-decoration-color: ${themeColors.colorLink};
  font-weight: 600;
`;

const TAndC = styled(TAndCLink)`
  color: ${themeColors.colorLink};
  font-weight: 600;
`;
const ChecboxWrapper = styled.div`
  display: flex;
`;
const Agreement = ({ isMetal }) => {
  const dispatch = useDispatch();
  const { onSuccess } = useStepPost({ nextPath: PAGES.KYC_PROCESSING });
  const { activeMenu, activeItem } = useMenu();
  const { t } = useTranslation();
  const { control, watch, setValue } = useForm();
  const { ApplicationUid } = useApplicationUid();
  const ApplicantData = useApplicantData();
  const { title } = useAgreement();
  const { gtmEvent } = useGtmHook();
  const settings = useSelector(selectSettings);
  const { MetalStreamRecurringInvestment } = useSelector(selectSettings);
  const Application = useSelector(selectApplication);
  const { AccountUpgradeType } = Application;
  const [isHaaAgreementOpen, setIsHaaAgreementOpen] = useState(false);
  const [isMetalStreamAgreementOpen, setIsMetalStreamAgreementOpen] = useState(
    false,
  );
  const [isACHAgreementOpen, setIsACHAgreementOpen] = useState(false);
  const bankAccounts = useSelector(selectBankAccounts);
  const [bankAccount, setBankAccount] = useState({});
  const [brokerCode, setBrokerCode] = useState('');
  const [brokerName, setBrokerName] = useState('');
  const { MetalStreamData } = useMetalStreamData();
  const BankAccountUid = metalStreamDataHelper(
    MetalStreamData,
    'BankAccountUid',
  );
  const UseAccountBalance = metalStreamDataHelper(
    MetalStreamData,
    'UseAccountBalance',
  );

  const isUpgrade =
    AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (
      ApplicantData?.PersonName &&
      ApplicantData?.PersonBirthday &&
      ApplicantData?.PersonAddress
    ) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Agreement',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        accountPhone: ApplicantData?.Phone,
        accountAddress: ApplicantData?.PersonAddress,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Agreement',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
    });
  };

  useEffect(() => {
    if (BankAccountUid) {
      if (BankAccountUid && !isEmpty(bankAccounts)) {
        const selectedBankAccount = bankAccounts.find(
          (bank) => bank.BankAccountUid === BankAccountUid,
        );
        if (selectedBankAccount) {
          setBankAccount(selectedBankAccount);
        }
      }
    }
  }, [BankAccountUid, bankAccounts]);

  useEffect(() => {
    if (!UseAccountBalance) {
      dispatch(
        fetchBankAccounts({
          ApplicationUid,
        }),
      );
    }
  }, [ApplicationUid, UseAccountBalance, BankAccountUid, dispatch]);

  useEffect(() => {
    if (settings && !isEmpty(settings)) {
      const { BrokerCode, BrokerName } = settings;
      setBrokerCode(BrokerCode);
      setBrokerName(BrokerName);
    }
  }, [settings]);
  const onSubmit = () => {
    const onSuccessAPI = () => {
      gtmEvent(APPLICATION_TERMS_ACCEPTED);
      handleGtmEvent();
      onSuccess();
    };

    const agreeToHaaTerms = () => {
      if (isMetal) {
        gtmEvent(APPLICATION_TERMS_ACCEPTED);
      }
      dispatch(
        createAgreement({
          ApplicationUid,
          type: AGREEMENT_TYPES.HAA,
          onSuccess: onSuccessAPI,
        }),
      );
    };

    if (isMetal) {
      const agreeToMetalStream = () => {
        dispatch(
          createAgreement({
            ApplicationUid,
            type: AGREEMENT_TYPES.METAL_STREAM,
            onSuccess: agreeToHaaTerms,
          }),
        );
      };

      const agreeToMetalStremaWithAutoDeposit = () => {
        dispatch(
          createAgreement({
            ApplicationUid,
            type: AGREEMENT_TYPES.ACH,
            onSuccess: agreeToMetalStream,
          }),
        );
      };

      if (!isEmpty(bankAccount)) {
        agreeToMetalStremaWithAutoDeposit();
        return;
      }

      agreeToMetalStream();
      return;
    }
    agreeToHaaTerms();
  };

  const standardTerms = watch('agree');
  const achTerms = watch('agreeAutoDeposit');

  const isOpenAccountButtonDisabled = () => {
    if (isMetal) {
      if (!isEmpty(bankAccount)) {
        return standardTerms && achTerms;
      }
      return standardTerms;
    }

    return standardTerms;
  };

  const onDownload = () => {
    dispatch(
      downloadPdfAgreement({
        ApplicationUid,
        type: AGREEMENT_TYPES.HAA,
      }),
    );
  };

  return (
    <>
      <OnBoardingContainer
        activeMenu={activeMenu}
        activeStep={activeItem}
        previousPath={activeItem?.previousPath}
        backSkipPath={activeItem?.preSkipPath}
      >
        <Helmet>
          <title>
            {t('agreement.titleTag', {
              brokerName,
            })}
          </title>
        </Helmet>
        <Heading mb={24}>{title}</Heading>
        <Paragraph mb={20} fontWeight={600}>
          {t('agreement.continuing')}
        </Paragraph>
        <Paragraph mb={8}>
          {t('agreement.paragraph1')}{' '}
          <Anchor
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              setIsHaaAgreementOpen(true);
            }}
          >
            <Strong>
              {`${t('agreement.paragraph1Link', {
                brokerName,
                brokerCode,
              })}`}
            </Strong>
          </Anchor>
          {'. '}
          {t('agreement.paragraph1b')}{' '}
        </Paragraph>
        <BulletList mb={8}>
          {isMetal && (
            <BulletItem variant="regular">
              <Paragraph fontWeight={400}>
                <Trans
                  i18nKey="agreement.bulletpoints.metal"
                  values={{ day: MetalStreamRecurringInvestment }}
                  components={{
                    bold: <Strong />,
                  }}
                />
              </Paragraph>
            </BulletItem>
          )}
          <BulletItem
            variant="regular"
            text={t('agreement.bulletpoints.point1')}
            fontWeight={400}
          />

          <BulletItem
            variant="regular"
            fontWeight={400}
            text={t('agreement.bulletpoints.point2')}
          />

          <BulletItem
            variant="regular"
            fontWeight={400}
            text={t('agreement.bulletpoints.point3', {
              brokerCode,
            })}
          />

          <BulletItem
            variant="regular"
            fontWeight={400}
            text={t('agreement.bulletpoints.point4')}
          />

          <BulletItem
            variant="regular"
            fontWeight={400}
            text={t('agreement.bulletpoints.point5')}
          />
        </BulletList>
        <Paragraph mb={8}>{t('agreement.paragraph2')}</Paragraph>
        <Paragraph mb={8}>
          {t('agreement.paragraph3', {
            brokerCode,
          })}
        </Paragraph>
        <Paragraph>
          {t('agreement.paragraph4', {
            brokerCode,
          })}
        </Paragraph>
        <FormWrap mt={16} isColumn>
          <DownloadContainer
            mb={16}
            onClick={() => {
              onDownload();
            }}
            color={themeColors.colorLink}
          >
            <DownloadIcon />
            <Paragraph
              variant="normal"
              fontWeight={600}
              color={themeColors.colorLink}
            >
              {t('agreement.download')}
            </Paragraph>
          </DownloadContainer>

          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                label={
                  <>
                    {t('agreement.acknowledge')}{' '}
                    <Anchor
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        setIsHaaAgreementOpen(true);
                      }}
                    >
                      <Strong>
                        {`${t('agreement.haaTerms', {
                          brokerName,
                        })}`}
                      </Strong>
                    </Anchor>
                  </>
                }
                onClick={() => {
                  onChange(!value);
                }}
                value={value}
                dataCy="checkbox-haa-agreement"
              />
            )}
            name="agree"
            defaultValue={false}
          />
          {isMetal && !isEmpty(bankAccount) && (
            <ChecboxWrapper>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    onClick={() => {
                      onChange(!value);
                    }}
                    value={value}
                  />
                )}
                name="agreeAutoDeposit"
              />
              <Paragraph>
                {t('fundAccount.agreeToAuthorize', {
                  bankName: bankAccount?.BankName,
                  accountNum: bankAccount?.AccountNumber?.substr(
                    (bankAccount?.AccountNumber).length - 4,
                  ),
                  brokerCode,
                })}
                <TAndC onClick={() => setIsACHAgreementOpen(true)}>
                  {t('fundAccount.transferTerms')}
                </TAndC>
              </Paragraph>
            </ChecboxWrapper>
          )}
        </FormWrap>
        <OnBoardingCta
          title={
            isUpgrade
              ? t('agreement.upgradeAccount')
              : t('agreement.openAccount')
          }
          onClick={onSubmit}
          disabled={!isOpenAccountButtonDisabled()}
          buttonProps={{
            'data-cy': 'button-open-account',
          }}
        />
      </OnBoardingContainer>
      <PrintModal
        isOpen={isHaaAgreementOpen}
        onClose={() => setIsHaaAgreementOpen(false)}
        onCloseButton={() => {
          setValue('agree', true, {
            shouldDirty: true,
          });
          setIsHaaAgreementOpen(false);
        }}
        buttonText={t('common.accept')}
        variant="lg"
        hidePrint
      >
        <AgreementDisplay type={AGREEMENT_TYPES.HAA} />
      </PrintModal>
      <PrintModal
        isOpen={isMetalStreamAgreementOpen}
        onClose={() => setIsMetalStreamAgreementOpen(false)}
        onCloseButton={() => {
          setValue('agreeMetalTc', true, {
            shouldDirty: true,
          });
          setIsMetalStreamAgreementOpen(false);
        }}
        buttonText={t('common.accept')}
        variant="lg"
        hidePrint
      >
        <AgreementDisplay type={AGREEMENT_TYPES.METAL_STREAM} />
      </PrintModal>
      <PrintModal
        isOpen={isACHAgreementOpen}
        onClose={() => {
          setIsACHAgreementOpen(false);
          setValue('agreeAutoDeposit', false, {
            shouldDirty: true,
          });
        }}
        onCloseButton={() => {
          setValue('agreeAutoDeposit', true, {
            shouldDirty: true,
          });
          setIsACHAgreementOpen(false);
        }}
        buttonText={t('common.accept')}
        variant="lg"
        hidePrint
      >
        <AgreementDisplay type={AGREEMENT_TYPES.ACH} />
      </PrintModal>
    </>
  );
};

Agreement.propTypes = {
  isMetal: PropTypes.bool,
};

Agreement.defaultProps = {
  isMetal: false,
};

export default Agreement;
